import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  InfoHint,
  Input,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Select,
  Table,
  TCell,
  Textarea,
  THead,
  TRow,
  cn,
} from '@cou/admin-ui';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  defaultMetricsClient,
  type MetricsClient,
  type SettlementMetrics,
} from '../settlement/metrics-client';
import {
  defaultContestsClient,
  type AdminContestWithEntryCount,
  type ContestsClient,
} from '../contests/contests-client';
import { contestDetails } from '../contests/contest-details';
import {
  defaultSettlementClient,
  type ContestMarket,
  type RescoreEdit,
  type SettlementClient,
} from '../settlement/settlement-client';

/**
 * The Settlement workbench (admin-ui.md → Settlement): the API-24 monitoring strip (COU-S35) over
 * the per-contest correction grid (API-11).
 *
 * The strip is counts only, grouped as the design lays them out (Entries, Staged results, Awards);
 * each number's queue lives elsewhere (failed awards → the Awards section). Below it, the correction
 * grid lists the selected Contest's markets with their current results: the operator edits any
 * market's result in place and runs the correction, which re-grades only the not-yet-settled Entries
 * — a settled Entry's Award is never clawed back (GL-20). Markets carrying a post-maturity
 * correction, or that closed without grading, are flagged for attention. Gated on `contests:settle`.
 */

export interface SettlementScreenProps {
  /** Injected for tests; defaults to the real API-24 client. */
  readonly client?: MetricsClient;
  /** Injected for tests; sources the contest-filter chips (API-09). */
  readonly contestsClient?: ContestsClient;
  /** Injected for tests; defaults to the real API-11 tooling client. */
  readonly settlementClient?: SettlementClient;
}

/**
 * How many contests to list in the filter dropdown. A dropdown (not chips) so the control stays
 * usable across a season of daily contests; "All contests" is the default and always aggregates
 * every contest globally regardless of this cap — the cap only bounds which contests are
 * individually selectable.
 */
const CONTEST_OPTION_LIMIT = 100;

export function SettlementScreen({
  client = defaultMetricsClient,
  contestsClient = defaultContestsClient,
  settlementClient = defaultSettlementClient,
}: SettlementScreenProps) {
  const { getToken } = useAdminAuth();
  const [metrics, setMetrics] = useState<SettlementMetrics | null>(null);
  const [contests, setContests] = useState<readonly AdminContestWithEntryCount[]>([]);
  const [contestId, setContestId] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  // The contest-filter source loads once — the strip is "global and filterable to a Contest".
  useEffect(() => {
    const token = getToken();
    if (token === null) return;
    let cancelled = false;
    void (async () => {
      const result = await contestsClient.listContests(token, { limit: CONTEST_OPTION_LIMIT });
      if (!cancelled && result.ok) setContests(result.contests);
    })();
    return () => {
      cancelled = true;
    };
  }, [contestsClient, getToken]);

  const load = useCallback(async () => {
    const token = getToken();
    if (token === null) return;
    const result = await client.getSettlementMetrics(
      token,
      contestId === 'all' ? undefined : contestId,
    );
    if (result.ok) {
      setMetrics(result.metrics);
      setError(null);
    } else {
      // Never render zeroes on a failed read: "0 stuck" is an answer, and it would be a lie.
      setError(result.message ?? 'The settlement metrics failed to load.');
      setMetrics(null);
    }
  }, [client, getToken, contestId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedContest = contests.find((c) => c.id === contestId);

  const contestOptions = [
    { value: 'all', label: 'All contests' },
    ...contests.map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <AdminShell
      title="Settlement"
      subtitle="Settlement health across contests, and the per-contest correction grid — edit any market's result and re-grade the affected entries."
    >
      {/* The ⓘ describes the Contest the strip is currently scoped to. "All contests" is an
          aggregate, not an entity, so it carries no hint. */}
      <div className="mt-1 flex items-end gap-2">
        <Select
          label="Contest"
          className="w-[280px]"
          options={contestOptions}
          value={contestId}
          onChange={setContestId}
        />
        {selectedContest && (
          <span className="pb-2.5">
            <InfoHint entity="Contest" details={contestDetails(selectedContest)} side="right" />
          </span>
        )}
      </div>

      {error !== null && (
        <Banner data-testid="settlement-error" variant="danger" className="mt-4">
          {error}
        </Banner>
      )}

      {metrics !== null && (
        <>
          <div
            data-testid="settlement-strip"
            className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3"
          >
            <StatGroup title="Entries">
              <Stat
                testId="metric-entries-settled"
                label="Settled"
                value={metrics.entries.settled}
              />
              <Stat
                testId="metric-entries-pending"
                label="Pending"
                value={metrics.entries.pending}
                tone="warn"
              />
              <Stat
                testId="metric-entries-stuck"
                label="Stuck"
                value={metrics.entries.stuck}
                tone="danger"
              />
            </StatGroup>

            <StatGroup title="Staged results">
              <Stat testId="metric-staged-held" label="Held" value={metrics.stagedResults.held} />
              <Stat
                testId="metric-staged-matured"
                label="Matured"
                value={metrics.stagedResults.matured}
              />
              <Stat
                testId="metric-staged-corrected"
                label="Corrected"
                value={metrics.stagedResults.corrected}
                tone="warn"
              />
            </StatGroup>

            <StatGroup title="Awards">
              <Stat
                testId="metric-awards-failed"
                label="Failed"
                value={metrics.awards.failed}
                tone="danger"
                href="/awards?status=Failed"
              />
              <Stat
                testId="metric-awards-retryable"
                label="Retryable"
                value={metrics.awards.retryable}
                tone="warn"
              />
            </StatGroup>
          </div>

          <p data-testid="settlement-as-of" className="mt-2 text-xs text-ink-4">
            Counted {new Date(metrics.asOf).toLocaleString()}
          </p>
        </>
      )}

      <CorrectionGrid client={settlementClient} contestId={contestId} />
    </AdminShell>
  );
}

/** The editable per-market draft: the corrected score (as typed) and whether the row is a void. */
interface MarketEdit {
  readonly score: string;
  readonly voided: boolean;
}

/**
 * A grid row's identity. The same external market can be snapshotted into several Contests, so the
 * external id alone is not unique in the "All contests" grid — keying drafts by it would let an
 * edit on one Contest's row silently drive another Contest's.
 */
const rowKey = (m: ContestMarket): string => `${m.contestId}:${m.externalMarketId}`;

/**
 * The per-contest correction grid (admin-ui.md → Settlement): the editable list of a Contest's
 * markets with their current results. The operator edits any row (a corrected score, or a void),
 * then runs the correction — which re-grades only the not-yet-settled Entries and recomputes their
 * counters. An already-settled Entry is left untouched (a delivered Award is never clawed back,
 * GL-20). Markets carrying a post-maturity correction, or that closed without grading, are flagged;
 * a correction can be acknowledged in place.
 */
/**
 * The pre-run impact of the edited rows. An Entry holds picks on many markets, so an Entry with a
 * pick on two edited markets lands in both markets' counts: the sums are an upper bound, not the
 * distinct-entry count the run itself reports. An Entry belongs to exactly one Contest, though, so
 * two edited markets can only overlap when they sit in the same Contest — which is what `count`
 * tests before hedging a total as "up to N", leaving the common single-market-per-Contest case
 * stated exactly.
 */
function impactOf(changed: readonly ContestMarket[]): {
  readonly pending: number;
  readonly settled: number;
  readonly count: (n: number) => string;
} {
  const editsPerContest = new Map<string, number>();
  for (const m of changed) {
    editsPerContest.set(m.contestId, (editsPerContest.get(m.contestId) ?? 0) + 1);
  }
  const exact = [...editsPerContest.values()].every((n) => n <= 1);
  return {
    pending: changed.reduce((n, m) => n + m.pendingEntries, 0),
    settled: changed.reduce((n, m) => n + m.settledEntries, 0),
    count: (n) => (exact ? String(n) : `up to ${n}`),
  };
}

function CorrectionGrid({
  client,
  contestId,
}: {
  readonly client: SettlementClient;
  readonly contestId: string;
}) {
  const { getToken } = useAdminAuth();
  const [markets, setMarkets] = useState<ContestMarket[] | null>(null);
  const [edits, setEdits] = useState<Record<string, MarketEdit>>({});
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; message: string } | null>(null);
  const [reason, setReason] = useState('');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // "All contests" spans every still-correctable Contest rather than showing nothing; a market is
  // still only correctable within its own Contest, so each row carries the Contest it belongs to.
  const allContests = contestId === 'all';

  const load = useCallback(async () => {
    const token = getToken();
    if (token === null) {
      setMarkets(null);
      return;
    }
    const result = await client.listContestMarkets(token, allContests ? null : contestId);
    if (result.ok) {
      setMarkets(result.markets);
      // Rows start read-only: a draft exists only for a row the operator has opened, so the grid
      // reads as a review surface and the rows under correction are the ones that look different.
      setEdits({});
      setError(null);
    } else {
      // Empty and "failed to load" are opposite answers; never render a false empty grid.
      setError(result.message ?? 'The contest markets failed to load.');
      setMarkets(null);
    }
  }, [client, getToken, contestId, allContests]);

  useEffect(() => {
    setFeedback(null);
    setReason('');
    void load();
  }, [load]);

  const setEdit = (id: string, patch: Partial<MarketEdit>) =>
    setEdits((cur) => ({
      ...cur,
      [id]: { ...(cur[id] ?? { score: '', voided: false }), ...patch },
    }));

  /** Open a row for correction, seeded from its current result (or from the feed's correction). */
  const openEdit = (m: ContestMarket, seed?: MarketEdit) =>
    setEdits((cur) => ({
      ...cur,
      [rowKey(m)]: seed ?? {
        score: m.result === null ? '' : String(m.result),
        voided: m.voided,
      },
    }));

  /** Close a row back to read-only, discarding its draft. */
  const closeEdit = (id: string) =>
    setEdits((cur) => {
      const { [id]: _dropped, ...rest } = cur;
      return rest;
    });

  // A row is an edit when its draft differs from the market's current graded value.
  const isEdited = (m: ContestMarket): boolean => {
    const e = edits[rowKey(m)];
    if (e === undefined) return false;
    if (e.voided !== m.voided) return true;
    return !e.voided && e.score.trim() !== (m.result === null ? '' : String(m.result));
  };
  const changed = (markets ?? []).filter(isEdited);

  const toRescoreEdit = (m: ContestMarket): RescoreEdit => {
    const e = edits[rowKey(m)]!;
    return {
      externalMarketId: m.externalMarketId,
      score: e.voided || e.score.trim() === '' ? null : Number(e.score),
      voided: e.voided,
    };
  };

  const impact = impactOf(changed);

  async function onRun() {
    const token = getToken();
    if (token === null) return;
    setRunning(true);
    // A rescore is atomic *per Contest* (one transaction each), so edits spanning the "All
    // contests" grid go one call per Contest. Contests are independent — a later Contest failing
    // leaves the earlier ones applied, which the feedback reports rather than hides.
    const byContest = new Map<string, ContestMarket[]>();
    for (const m of changed) {
      const bucket = byContest.get(m.contestId);
      if (bucket === undefined) byContest.set(m.contestId, [m]);
      else bucket.push(m);
    }

    const totals = { marketsApplied: 0, entriesRegraded: 0, entriesSkipped: 0 };
    const failures: string[] = [];
    for (const [id, group] of byContest) {
      const result = await client.rescoreContest(
        token,
        id,
        group.map(toRescoreEdit),
        reason.trim(),
      );
      if (result.ok) {
        totals.marketsApplied += result.result.marketsApplied;
        totals.entriesRegraded += result.result.entriesRegraded;
        totals.entriesSkipped += result.result.entriesSkipped;
      } else {
        const name = group[0]?.contestName ?? id;
        failures.push(`${name}: ${result.message ?? 'the correction failed'}`);
      }
    }
    setRunning(false);
    setConfirming(false);

    const applied = `Re-graded ${totals.entriesRegraded} ${totals.entriesRegraded === 1 ? 'entry' : 'entries'} across ${totals.marketsApplied} market(s); ${totals.entriesSkipped} settled skipped.`;
    if (failures.length === 0) {
      setFeedback({ kind: 'ok', message: applied });
      setReason('');
    } else {
      setFeedback({
        kind: 'err',
        message:
          totals.marketsApplied === 0
            ? failures.join(' · ')
            : `${applied} Some contests failed — ${failures.join(' · ')}`,
      });
    }
    await load();
  }

  async function onAcknowledge(m: ContestMarket) {
    const token = getToken();
    if (token === null) return;
    const result = await client.acknowledgeCorrection(token, m.externalMarketId);
    if (result.ok) {
      // The staged row is keyed by external market id, not by Contest — so acknowledging clears
      // the flag on every row for that market, including the same market in another Contest.
      setMarkets((cur) =>
        cur === null
          ? cur
          : cur.map((x) =>
              x.externalMarketId === m.externalMarketId
                ? { ...x, correctionAcknowledged: true }
                : x,
            ),
      );
    } else {
      setError(result.message ?? 'The acknowledge failed.');
    }
  }

  const rows = (markets ?? []).filter((m) => !attentionOnly || m.status !== 'ok');

  return (
    <section className="mt-8" data-testid="correction-grid">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-ui text-sm font-semibold text-ink-2">Corrections</h2>
          <p className="mt-1 text-xs text-ink-4">
            Edit any market&apos;s result, then run the correction. Only not-yet-settled entries are
            re-graded — a settled entry&apos;s award is never clawed back (GL-20).
          </p>
        </div>
        {markets !== null && markets.length > 0 && (
          <Checkbox
            label="Needs attention only"
            checked={attentionOnly}
            onCheckedChange={(v) => setAttentionOnly(v === true)}
          />
        )}
      </div>

      {feedback !== null && (
        <Banner
          data-testid="grid-feedback"
          variant={feedback.kind === 'ok' ? 'success' : 'danger'}
          className="mt-3"
        >
          {feedback.message}
        </Banner>
      )}
      {error !== null && (
        <Banner data-testid="grid-error" variant="danger" className="mt-3">
          {error}
        </Banner>
      )}

      {error === null && markets !== null && markets.length === 0 && (
        <Card data-testid="grid-empty" className="mt-3 p-5 text-sm text-ink-4">
          {allContests ? 'No contests have correctable markets.' : 'This contest has no markets.'}
        </Card>
      )}

      {markets !== null && markets.length > 0 && (
        <>
          <Table
            data-testid="market-grid"
            className="mt-3"
            // Result carries the value, its edit controls, the diff and the feed's correction, so
            // it needs real width. Status holds the flag pill *and* the review button, which must
            // fit side by side or it paints over Impact. The leading Contest track only exists in
            // the aggregate view, where a row needs attributing.
            columns={cn(
              allContests && 'minmax(0,1fr)',
              'minmax(0,1fr) minmax(0,1.1fr) 56px minmax(280px,1.4fr) 210px 150px',
            )}
          >
            <THead>
              {allContests && <TCell>Contest</TCell>}
              <TCell>Event</TCell>
              <TCell>Subject</TCell>
              <TCell>Line</TCell>
              <TCell>Result</TCell>
              <TCell>Status</TCell>
              <TCell>Impact</TCell>
            </THead>
            {rows.map((m) => {
              const key = rowKey(m);
              const draft = edits[key];
              const open = draft !== undefined;
              const dirty = isEdited(m);
              const feed = parseCorrection(m.correction);
              return (
                <TRow
                  key={key}
                  data-testid={`market-row-${m.externalMarketId}`}
                  // A row under correction is marked where the operator is looking, so a batch
                  // stays legible while scrolling instead of living only in the footer counter.
                  className={cn(dirty && 'bg-warn/[0.07]')}
                >
                  {allContests && <TCell>{m.contestName}</TCell>}
                  <TCell>{m.eventName}</TCell>
                  <TCell>{m.subject}</TCell>
                  <TCell variant="num">{m.line}</TCell>
                  <TCell>
                    <div className="flex flex-col gap-1.5 py-0.5">
                      {open ? (
                        <div className="flex items-center gap-2">
                          <Input
                            aria-label={`New score for ${m.subject}`}
                            type="number"
                            className="w-[88px]"
                            value={draft.score}
                            disabled={draft.voided}
                            onChange={(ev) => setEdit(key, { score: ev.target.value })}
                          />
                          <Checkbox
                            label="Void"
                            aria-label={`Void ${m.subject}`}
                            checked={draft.voided}
                            onCheckedChange={(v) => setEdit(key, { voided: v === true })}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`cancel-edit-${m.externalMarketId}`}
                            onClick={() => closeEdit(key)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[13px] tabular-nums text-ink">
                            {m.voided ? 'Void' : m.result === null ? '—' : m.result}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`edit-${m.externalMarketId}`}
                            onClick={() => openEdit(m)}
                          >
                            Correct
                          </Button>
                        </div>
                      )}

                      {dirty && draft !== undefined && (
                        <span
                          data-testid={`diff-${m.externalMarketId}`}
                          className="font-mono text-[12px] tabular-nums text-warn"
                        >
                          {m.voided ? 'Void' : (m.result ?? '—')} →{' '}
                          {draft.voided ? 'Void' : draft.score.trim() === '' ? '—' : draft.score}
                        </span>
                      )}

                      {feed !== null && (
                        <CorrectionNote
                          feed={feed}
                          testId={m.externalMarketId}
                          onApply={() =>
                            openEdit(m, {
                              score: feed.score === null ? '' : String(feed.score),
                              voided: feed.voided,
                            })
                          }
                        />
                      )}
                    </div>
                  </TCell>
                  <TCell>
                    <div className="flex items-center gap-2">
                      <StatusFlag status={m.status} />
                      {m.status === 'correction' && !m.correctionAcknowledged && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`acknowledge-${m.externalMarketId}`}
                          onClick={() => void onAcknowledge(m)}
                        >
                          Mark reviewed
                        </Button>
                      )}
                    </div>
                  </TCell>
                  <TCell>
                    <span className="text-xs text-ink-4">
                      {m.pendingEntries} pending · {m.settledEntries} settled
                    </span>
                  </TCell>
                </TRow>
              );
            })}
          </Table>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              data-testid="run-correction"
              disabled={changed.length === 0 || running}
              onClick={() => setConfirming(true)}
            >
              Review correction
            </Button>
            <span data-testid="impact-preview" className="text-sm text-ink-4">
              {changed.length === 0
                ? 'No edits yet.'
                : `${changed.length} market(s) edited — ${impact.count(impact.pending)} ${impact.pending === 1 ? 'entry' : 'entries'} will re-grade, ${impact.count(impact.settled)} settled skipped.`}
            </span>
          </div>
        </>
      )}

      {confirming && (
        <ConfirmCorrectionModal
          changed={changed}
          reason={reason}
          onReasonChange={setReason}
          running={running}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void onRun()}
        />
      )}
    </section>
  );
}

/**
 * The confirm step in front of a correction. It exists because the consequence is asymmetric and
 * easy to miss: the re-grade reaches only not-yet-settled Entries, and every already-settled Entry
 * keeps its wrong result because its Award has already been delivered (GL-20). That trade is stated
 * here, per Contest, at the moment of committing — and the audit reason is captured here too, so
 * the operator writes it while looking at what it describes.
 */
function ConfirmCorrectionModal({
  changed,
  reason,
  onReasonChange,
  running,
  onCancel,
  onConfirm,
}: {
  readonly changed: readonly ContestMarket[];
  readonly reason: string;
  readonly onReasonChange: (value: string) => void;
  readonly running: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const byContest = new Map<string, ContestMarket[]>();
  for (const m of changed) {
    const bucket = byContest.get(m.contestName);
    if (bucket === undefined) byContest.set(m.contestName, [m]);
    else bucket.push(m);
  }
  const { pending: regrade, settled: skipped, count } = impactOf(changed);

  return (
    <Modal open onClose={onCancel} maxWidth={620}>
      <ModalHead
        title="Run this correction?"
        subtitle={`${changed.length} market(s) across ${byContest.size} contest(s) will be re-graded.`}
        onClose={onCancel}
      />
      <ModalBody>
        <div className="flex flex-col gap-3.5 px-7 py-5">
          {skipped > 0 && (
            <Banner
              variant="warn"
              title={`${count(skipped)} settled ${skipped === 1 ? 'entry' : 'entries'} keep the old result`}
            >
              Their awards have already been delivered, so a re-grade never claws them back (GL-20).
              Only the {count(regrade)} not-yet-settled {regrade === 1 ? 'entry' : 'entries'} will
              change.
            </Banner>
          )}

          <div data-testid="confirm-breakdown" className="flex flex-col gap-2.5">
            {[...byContest].map(([contestName, ms]) => (
              <div key={contestName} className="flex flex-col gap-1">
                <span className="font-ui text-[12px] font-semibold text-ink-2">{contestName}</span>
                {ms.map((m) => (
                  <span key={m.externalMarketId} className="text-[12px] text-ink-4">
                    {m.subject} · {m.marketType} {m.line} — {m.pendingEntries} re-grade,{' '}
                    {m.settledEntries} skipped
                  </span>
                ))}
              </div>
            ))}
          </div>

          <Textarea
            label="Reason"
            value={reason}
            onChange={(ev) => onReasonChange(ev.target.value)}
          />
        </div>
      </ModalBody>
      <ModalFoot summary={running ? 'Running…' : undefined}>
        <Button variant="outline" onClick={onCancel} disabled={running}>
          Cancel
        </Button>
        <Button
          data-testid="confirm-correction"
          disabled={reason.trim() === '' || running}
          onClick={onConfirm}
        >
          Run correction
        </Button>
      </ModalFoot>
    </Modal>
  );
}

/** A small status pill flagging whether a market needs the operator's attention. */
/** What the feed's post-maturity correction actually said (staged, never applied — GL-20). */
interface FeedCorrection {
  readonly reason: string;
  readonly score: number | null;
  readonly voided: boolean;
}

/** The human-facing name of each recorded correction reason (ingest.ts → CorrectionReason). */
const CORRECTION_REASONS: Record<string, string> = {
  post_maturity_score: 'Score revised after maturity',
  post_maturity_void: 'Voided after maturity',
  post_maturity_reconfirm: 'Re-confirmed after maturity',
  post_maturity_unconfirm: 'Unconfirmed after maturity',
};

/**
 * Read the staged correction blob the ingest recorded. It is typed as an open record on the wire,
 * so every field is checked rather than trusted — an unrecognised shape degrades to "no verdict to
 * show" instead of rendering `undefined` at the operator.
 */
function parseCorrection(correction: Record<string, unknown> | null): FeedCorrection | null {
  if (correction === null) return null;
  const rawReason = correction.reason;
  const reason =
    typeof rawReason === 'string'
      ? (CORRECTION_REASONS[rawReason] ?? rawReason)
      : 'Correction recorded';
  const score = typeof correction.score === 'number' ? correction.score : null;
  return { reason, score, voided: correction.voided === true };
}

/**
 * The feed's recorded verdict on a corrected market, and the one-click way to take it. Showing it
 * is the point of the queue: without it the operator is told a correction arrived but not what it
 * said, and has to retype a value they cannot see. Applying still only stages the edit — the
 * correction is not run until the operator confirms the batch (GL-20: never auto-applied).
 */
function CorrectionNote({
  feed,
  testId,
  onApply,
}: {
  readonly feed: FeedCorrection;
  readonly testId: string;
  readonly onApply: () => void;
}) {
  const verdict = feed.voided ? 'Void' : feed.score === null ? null : String(feed.score);
  return (
    <div data-testid={`correction-note-${testId}`} className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] text-ink-4">
        {verdict === null ? feed.reason : `Feed says ${verdict} — ${feed.reason}`}
      </span>
      {verdict !== null && (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`apply-feed-${testId}`}
          onClick={onApply}
          className="h-auto px-1.5 py-0 text-[11px]"
        >
          Apply
        </Button>
      )}
    </div>
  );
}

function StatusFlag({ status }: { readonly status: ContestMarket['status'] }) {
  const tone =
    status === 'correction'
      ? 'bg-warn/15 text-warn'
      : status === 'ungraded'
        ? 'bg-danger/15 text-danger'
        : 'bg-line text-ink-4';
  const label = status === 'correction' ? 'Correction' : status === 'ungraded' ? 'Ungraded' : 'OK';
  return (
    <span
      data-testid={`status-${status}`}
      className={cn('rounded-[2px] px-1.5 py-0.5 text-[11px] font-medium', tone)}
    >
      {label}
    </span>
  );
}

/** One grouped monitoring card: an uppercase title over an evenly-divided row of stats. */
function StatGroup({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <Card className="gap-4 p-5">
      <span className="font-ui text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-3">
        {title}
      </span>
      <div className="mt-3 grid grid-cols-3 divide-x divide-line">{children}</div>
    </Card>
  );
}

/**
 * One stat cell. A `tone` colours the number only when it is non-zero — a coloured zero would cry
 * wolf; a zero reads as calm grey (mirrors KpiCell's idle treatment). `href` makes the number a
 * link into the queue that owns it (the failed-award count → the Awards section).
 */
function Stat({
  testId,
  label,
  value,
  tone = 'default',
  href,
}: {
  readonly testId: string;
  readonly label: string;
  readonly value: number;
  readonly tone?: 'default' | 'warn' | 'danger';
  readonly href?: string;
}) {
  const colour =
    value === 0
      ? 'text-ink-4'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-ink';
  const numberClass = cn(
    'font-mono text-[24px] font-semibold leading-none tracking-[-0.02em] tabular-nums',
    colour,
  );
  return (
    <div data-testid={testId} className="flex flex-col gap-1.5 px-4 first:pl-0">
      {href !== undefined ? (
        <Link to={href} data-testid={`${testId}-link`} className={numberClass}>
          {value}
        </Link>
      ) : (
        <span className={numberClass}>{value}</span>
      )}
      <span className="font-ui text-[12px] text-ink-4">{label}</span>
    </div>
  );
}
